import { useState } from "react";
import { Hand, Loader2, WifiOff, UserCheck, Undo2, Info, X } from "lucide-react";
import { toast } from "sonner";
import { claimJob, rememberClaim, fmtClaimTime } from "../lib/claim";

/* "I'm on it" card: first-to-claim wins. Needs network (the server is the referee). */
export const ClaimCard = ({ token, job, t, hash, online, released, onClaimed, onTaken }) => {
  const [name, setName] = useState(job.recipient_name || "");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return toast.error(t.nameRequired);
    setBusy(true);
    let res;
    try { res = await claimJob(token, hash, n.slice(0, 60), phone.trim()); } catch { res = null; }
    setBusy(false);
    if (!res) return toast.error(t.claimFailed);
    if (res.ok) { await rememberClaim(token, res.body.claimed_at); toast.success(t.claimedByYou.replace("{time}", fmtClaimTime(res.body.claimed_at))); onClaimed(res.body); return; }
    if (res.status === 409 && res.body.error === "already_claimed") return onTaken(res.body);
    if (res.status === 503) return toast.error(t.claimsUnavailable);
    if (res.status === 400 && res.body.error === "bad_phone") return toast.error(t.yourPhone);
    toast.error(t.claimFailed);
  };
  return (
    <section className="card mt-5 fade-up claim-card" data-testid="claim-card" data-released={released ? "true" : undefined}>
      {released && <p className="offline-badge is-offline mb-3" data-testid="claim-released-banner"><Undo2 size={12} /> {t.releasedBanner}</p>}
      <div className="flex items-center gap-2"><Hand className="text-primary" size={20} /><p className="font-bold text-lg">{released ? t.claimAgain : t.claimTitle}</p></div>
      <p className="text-sm text-muted mt-1">{t.claimBody}</p>
      <form onSubmit={submit} className="mt-3 space-y-2">
        <input className="field notranslate" translate="no" maxLength={60} placeholder={t.yourName} value={name} onChange={(e) => setName(e.target.value)} required data-testid="claim-name-input" />
        <input className="field notranslate" translate="no" type="tel" inputMode="tel" autoComplete="tel" maxLength={20} placeholder={t.yourPhone} value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="claim-phone-input" />
        <button type="submit" className="btn-primary flex items-center justify-center gap-2" disabled={busy || !online} data-testid="claim-button">
          {busy ? <><Loader2 className="animate-spin" size={16} /> {t.claiming}</> : !online ? <><WifiOff size={16} /> {t.connectToClaim}</> : <><Hand size={16} /> {t.imOnIt}</>}
        </button>
      </form>
    </section>
  );
};

const fill = (tpl, vars) => tpl.split(/(\{name\}|\{time\})/).map((p, i) => (p === "{name}" || p === "{time}") ? <span key={i} className="notranslate" translate="no">{p === "{name}" ? vars.name : vars.time}</span> : p);

export const TakenBanner = ({ job, t, justNow }) => (
  <p className="offline-badge is-taken mt-4" data-testid="claim-taken-banner"><UserCheck size={12} /> {fill(justNow ? t.takenJustNow : t.takenBy, { name: job.claimed_by_name || "—", time: job.claimed_at ? fmtClaimTime(job.claimed_at) : "" })}</p>
);

export const MineBanner = ({ job, t }) => (
  <p className="offline-badge is-saved mt-4" data-testid="claim-mine-banner"><UserCheck size={12} /> {fill(t.claimedByYou, { time: fmtClaimTime(job.claimed_at) })}</p>
);

/* Persistent (until dismissed) explanation for a queued action the server refused. */
export const QueueNotice = ({ notice, t, onDismiss }) => {
  if (!notice) return null;
  const text = notice.kind === "completed_by" ? t.noticeCompletedBy : notice.kind === "taken_by" ? t.noticeTakenBy : notice.kind === "not_claimed" ? t.noticeNotClaimed : t.proofAlreadySubmitted;
  const [a, b] = text.split("{name}");
  return (
    <div className="banner-danger mt-4 flex items-start gap-2" role="status" data-testid="queue-notice" data-kind={notice.kind}>
      <Info size={16} className="shrink-0 mt-0.5" />
      <span className="flex-1 text-sm">{a}{b !== undefined && <><span className="notranslate font-bold" translate="no">{notice.name || "—"}</span>{b}</>}</span>
      <button type="button" className="icon-btn" onClick={onDismiss} aria-label={t.dismiss} data-testid="queue-notice-dismiss"><X size={14} /></button>
    </div>
  );
};
